import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ProductFeedbackModalContainer } from '@app/web/src/containers/ProductFeedback/ProductFeedbackModalContainer';
import { useWidgetTasteMatchCatalogRedirect } from '@app/web/src/containers/WidgetLauncher/hooks/useWidgetTasteMatchCatalogRedirect';

import { useProductFeedback } from '@lib/core/products/hooks/useProductFeedback';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { prependBasename } from '@lib/core/service/utils';
import { AuthShortcutSession } from '@lib/core/service/utils/authShortcutSessionState';
import { SERVICE_TERMS_CATEGORIES } from '@lib/core/serviceTerms/consts';
import { useServiceTerms } from '@lib/core/serviceTerms/hooks/useServiceTerms';
import { useUser } from '@lib/core/users/hooks';
import { useModals } from '@lib/tools/modals/hooks';
import { RouteUtils } from '@lib/tools/routes';
import {
  LEGAL_PAGE_COOKIE_POLICY_PAGE,
  LEGAL_PAGE_PRIVACY_POLICY_PAGE,
  LEGAL_PAGE_TERMS_AND_CONDITIONS_PAGE,
  MODALS,
} from '@lib/tools/shared/helpers/consts';
import { useAddons } from '@lib/tools/views/hooks';
import { PAGES } from '@lib/tools/views/urls';

import { AuthShortcutPopup } from '@components/web/src/templates/LoginAndRegistration/AuthShortcutPopup/AuthShortcutPopup';
import { BasicServiceTermsModal } from '@components/web/src/templates/Modals/BasicServiceTermsModal/BasicServiceTermsModal';

const HocStart = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useWidgetTasteMatchCatalogRedirect();

  const { closeAllModals, openModal, isBasicServiceTermsModalOpened } = useModals();
  const { isDisableCookieManagementAddon } = useAddons();
  const { isUserAuthenticated } = useUser();

  const { productFeedbackModalOpenedForCurrentRtl } = useProductFeedback();
  const {
    updateServiceTerms,
    serviceTermsListUpdateIsLoading,
    isTermsInitialDataLoading,
    serviceTermsByCategory: { [SERVICE_TERMS_CATEGORIES.cookies]: cookies },
  } = useServiceTerms();
  const { isAnonymousRetailerAccess } = useRetailer();

  const [isGlobalAuthShortcutOpen, setIsGlobalAuthShortcutOpen] = useState(false);
  const [isGlobalAuthShortcutDismissed, setIsGlobalAuthShortcutDismissed] = useState(AuthShortcutSession.isDismissed());

  const isLegalPage = [
    LEGAL_PAGE_COOKIE_POLICY_PAGE,
    LEGAL_PAGE_TERMS_AND_CONDITIONS_PAGE,
    LEGAL_PAGE_PRIVACY_POLICY_PAGE,
    PAGES.vinhood.legal.generalServiceTerms,
  ].some(legalPage => RouteUtils.currentPageContains(legalPage));

  const isWidgetLauncherPage = RouteUtils.currentPageContains(PAGES.vinhood.widgetLauncher);
  const isRedirectPage = RouteUtils.currentPageContains(PAGES.vinhood.redirect);
  const isCatalogPage = RouteUtils.currentPageContains(PAGES.vinhood.catalog);
  const isLoginPage = RouteUtils.currentPageContains(PAGES.vinhood.signIn);
  const isRegistrationPage = RouteUtils.currentPageContains(PAGES.vinhood.registration);
  const isAuthPage = isLoginPage || isRegistrationPage;

  const shouldOpenBasicServiceTermsModal =
    !isWidgetLauncherPage &&
    !isRedirectPage &&
    !isLegalPage &&
    !isDisableCookieManagementAddon &&
    !cookies?.is_selected;

  const shouldShowGlobalAuthShortcut =
    !isAnonymousRetailerAccess &&
    !isUserAuthenticated &&
    !isGlobalAuthShortcutDismissed &&
    !isGlobalAuthShortcutOpen &&
    !isTermsInitialDataLoading &&
    !shouldOpenBasicServiceTermsModal &&
    !isBasicServiceTermsModalOpened &&
    !isWidgetLauncherPage &&
    !isRedirectPage &&
    !isLegalPage &&
    !isCatalogPage &&
    !isAuthPage;

  useEffect(() => {
    if (shouldOpenBasicServiceTermsModal) {
      openModal(MODALS.TERMS_OF_SERVICE_BASIC_MODAL);
    }
  }, [location, isTermsInitialDataLoading, cookies]);

  useEffect(() => {
    if (!isUserAuthenticated) {
      setIsGlobalAuthShortcutDismissed(AuthShortcutSession.isDismissed());
    }
  }, [isUserAuthenticated]);

  useEffect(() => {
    if ((isCatalogPage || isAuthPage) && isGlobalAuthShortcutOpen) {
      setIsGlobalAuthShortcutOpen(false);
    }
  }, [isCatalogPage, isGlobalAuthShortcutOpen, isAuthPage]);

  useEffect(() => {
    if (!shouldShowGlobalAuthShortcut) {
      return;
    }

    setIsGlobalAuthShortcutOpen(true);
  }, [shouldShowGlobalAuthShortcut]);

  const handleOpenGeneralServiceTermsPage = () => {
    closeAllModals();
    navigate(prependBasename(PAGES.vinhood.legal.generalServiceTerms));
  };

  const handleAcceptAllBasicServiceTerms = () => {
    updateServiceTerms([
      { category: SERVICE_TERMS_CATEGORIES.cookies, is_selected: true },
      { category: SERVICE_TERMS_CATEGORIES.profiling, is_selected: true },
    ]).then(() => closeAllModals());
  };

  const handleRejectOptionalBasicServiceTerms = () => {
    updateServiceTerms([
      { category: SERVICE_TERMS_CATEGORIES.cookies, is_selected: true },
      { category: SERVICE_TERMS_CATEGORIES.profiling, is_selected: false },
    ]).then(() => closeAllModals());
  };

  const handleDismissGlobalAuthShortcut = useCallback(() => {
    AuthShortcutSession.setDismissed();
    setIsGlobalAuthShortcutDismissed(true);
    setIsGlobalAuthShortcutOpen(false);
  }, []);

  return (
    <>
      {children}
      {isBasicServiceTermsModalOpened && shouldOpenBasicServiceTermsModal && (
        <BasicServiceTermsModal
          handleAcceptAllBasicServiceTerms={handleAcceptAllBasicServiceTerms}
          handleOpenGeneralServiceTermsPage={handleOpenGeneralServiceTermsPage}
          handleRejectOptionalBasicServiceTerms={handleRejectOptionalBasicServiceTerms}
          isDisabled={isTermsInitialDataLoading}
          isLoading={serviceTermsListUpdateIsLoading || isTermsInitialDataLoading}
          isModalOpen={isBasicServiceTermsModalOpened}
        />
      )}
      {!isAuthPage && <AuthShortcutPopup isOpen={isGlobalAuthShortcutOpen} onClose={handleDismissGlobalAuthShortcut} />}
      {productFeedbackModalOpenedForCurrentRtl && <ProductFeedbackModalContainer />}
    </>
  );
};

export default HocStart;
